import {
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  LinearProgress,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  MenuItem,
  TextField
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Close as CloseIcon } from "@material-ui/icons";
import { useQuery } from "@redux-requests/react";
import { Form, Formik } from "formik";
import { groupBy, isEmpty } from "lodash";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { Flaticon } from "../../components";
import { MeetingSlotSelectField, SubmitButton } from "../../components/formik";
import { types } from "../../ducks";
import { createMeeting, getProfileMeetingSlots } from "../../ducks/actions";
import useActionRequest from "../../hooks/useActionRequest";

const useStyles = makeStyles(theme => ({
  title: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(3)
  }
}));

const ScheduleMeetingDialog = ({ profileId, onClose, onMeetingCreated }) => {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const [selectedDay, setSelectedDay] = useState("");
  const [createMeetingLoading, setCreateMeetingLoading] = useState(false);

  useEffect(() => {
    dispatch(getProfileMeetingSlots({ profileId }));
  }, [dispatch, profileId]);

  const {
    data: { data: meetingSlots },
    loading: meetingSlotsLoading
  } = useQuery({
    type: types.GET_PROFILE_MEETING_SLOTS,
    defaultData: { data: [] },
    autoReset: true
  });

  const createMeetingRequest = useActionRequest({
    action: createMeeting
  });

  const onMeetingSlotSelect = async ({ meetingSlotId }) => {
    setCreateMeetingLoading(true);
    const { data } = await createMeetingRequest({ meetingSlotId });
    if (data) {
      setCreateMeetingLoading(false);
      onClose();
      onMeetingCreated({ id: data.data.id });
    }
  };

  const meetingSlotsByDay = groupBy(meetingSlots, ({ startAt }) =>
    moment(startAt).format("YYYY-MM-DD")
  );

  const noAvailableMeetingsAlert = (
    <Alert severity="info">
      {intl.formatMessage({
        id: "Experts.ScheduleMeetingDialog.NoAvailableMeetings",
        defaultMessage: "Sorry, there are no available meetings. Please try check later..."
      })}
    </Alert>
  );

  return (
    <Dialog open maxWidth="sm" fullWidth onClose={onClose}>
      <div className={classes.title}>
        <ListItem>
          <ListItemIcon>
            <Flaticon icon="calendar" fontSize="1.25rem" />
          </ListItemIcon>
          <ListItemText
            primary={
              <strong>
                {intl.formatMessage({
                  id: "Experts.ScheduleMeetingDialog.ScheduleYouLiveMeeting",
                  defaultMessage: "Schedule your live meeting"
                })}
              </strong>
            }
            primaryTypographyProps={{ variant: "h5" }}
          />
        </ListItem>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Formik initialValues={{ meetingSlotId: "" }} onSubmit={onMeetingSlotSelect}>
        <Form>
          <DialogContent>
            {!meetingSlotsLoading && isEmpty(meetingSlots) ? (
              noAvailableMeetingsAlert
            ) : (
              <>
                {meetingSlotsLoading || createMeetingLoading ? (
                  <LinearProgress />
                ) : (
                  <>
                    <TextField
                      select
                      label={intl.formatMessage({
                        id: "Experts.ScheduleMeetingDialog.Day",
                        defaultMessage: "Day"
                      })}
                      value={selectedDay}
                      fullWidth
                      onChange={({ target: { value } }) => setSelectedDay(value)}
                    >
                      {Object.keys(meetingSlotsByDay).map(day => (
                        <MenuItem key={day} value={day}>
                          {moment(day, "YYYY-MM-DD").format("DD MMMM YYYY")}
                        </MenuItem>
                      ))}
                    </TextField>
                    <MeetingSlotSelectField
                      name="meetingSlotId"
                      meetingSlots={meetingSlotsByDay[selectedDay]}
                    />
                  </>
                )}
              </>
            )}
          </DialogContent>
          <Divider />
          <DialogActions>
            <SubmitButton color="primary">
              {intl.formatMessage({
                id: "Exhibitions.ScheduleMeetingDialog.BookAppointment",
                defaultMessage: "Book an appointment"
              })}
            </SubmitButton>
          </DialogActions>
        </Form>
      </Formik>
    </Dialog>
  );
};

export default ScheduleMeetingDialog;
